import {
  CATEGORY_ID_CORAL,
  CATEGORY_ID_FISH,
  LISTING_TYPE_DRY_GOODS,
} from '../config/r2rListingTypeCategories';
import { PROFILE_TAB_LISTINGS } from './profilePageTabs';

export const PROFILE_LISTINGS_PAGE_SIZE = 50;
export const PROFILE_LISTINGS_MAX_PAGE = 500;
export const PROFILE_LISTINGS_SUBCATEGORY_ID = /^[a-zA-Z0-9_-]{1,80}$/;

export const PROFILE_LISTINGS_CATEGORY_CORALS = 'corals';
export const PROFILE_LISTINGS_CATEGORY_FISH = 'fish';
export const PROFILE_LISTINGS_CATEGORY_DRY_GOODS = 'dry_goods';

export const PROFILE_LISTINGS_CATEGORIES = [
  PROFILE_LISTINGS_CATEGORY_CORALS,
  PROFILE_LISTINGS_CATEGORY_FISH,
  PROFILE_LISTINGS_CATEGORY_DRY_GOODS,
];

export const PROFILE_LISTINGS_SORT_NEWEST = 'pub_publishedAt';
export const PROFILE_LISTINGS_SORT_OLDEST = '-pub_publishedAt';
export const PROFILE_LISTINGS_SORT_PRICE_LOW = '-price';
export const PROFILE_LISTINGS_SORT_PRICE_HIGH = 'price';

export const PROFILE_LISTINGS_SORTS_IN_URL = [
  PROFILE_LISTINGS_SORT_OLDEST,
  PROFILE_LISTINGS_SORT_PRICE_LOW,
  PROFILE_LISTINGS_SORT_PRICE_HIGH,
];

const parseSearchParams = search => {
  if (!search || typeof search !== 'string') {
    return new URLSearchParams();
  }
  return new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
};

const sanitizeSubcategory = (rawSubcategory, category, rawCategory) => {
  if (!rawSubcategory || !PROFILE_LISTINGS_SUBCATEGORY_ID.test(rawSubcategory)) {
    return null;
  }
  if (category && category !== PROFILE_LISTINGS_CATEGORY_CORALS) {
    return null;
  }
  if (!category && rawCategory && !PROFILE_LISTINGS_CATEGORIES.includes(rawCategory)) {
    return null;
  }
  return rawSubcategory;
};

export const parseProfileListingsSearch = search => {
  const params = parseSearchParams(search);
  const rawPage = Number.parseInt(params.get('page'), 10);
  const page =
    Number.isInteger(rawPage) && rawPage >= 1 ? Math.min(rawPage, PROFILE_LISTINGS_MAX_PAGE) : 1;

  const rawCategory = params.get('category');
  const category = PROFILE_LISTINGS_CATEGORIES.includes(rawCategory) ? rawCategory : null;

  const rawSort = params.get('sort');
  const sort = PROFILE_LISTINGS_SORTS_IN_URL.includes(rawSort) ? rawSort : null;

  const subcategory = sanitizeSubcategory(params.get('subcategory'), category, rawCategory);

  return { page, category, subcategory, sort };
};

export const stringifyProfileListingsSearch = ({
  tab,
  page = 1,
  category = null,
  subcategory = null,
  sort = null,
} = {}) => {
  const params = new URLSearchParams();
  if (tab && tab !== 'profile') {
    params.set('tab', tab);
  }
  if (page > 1) {
    params.set('page', String(page));
  }
  if (category && PROFILE_LISTINGS_CATEGORIES.includes(category)) {
    params.set('category', category);
  }
  if (
    subcategory &&
    PROFILE_LISTINGS_SUBCATEGORY_ID.test(subcategory) &&
    (!category || category === PROFILE_LISTINGS_CATEGORY_CORALS)
  ) {
    params.set('subcategory', subcategory);
  }
  if (sort && PROFILE_LISTINGS_SORTS_IN_URL.includes(sort)) {
    params.set('sort', sort);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const profileListingsSdkParams = ({ page, category, subcategory, sort }) => {
  const params = {
    page,
    perPage: PROFILE_LISTINGS_PAGE_SIZE,
    sort: sort || PROFILE_LISTINGS_SORT_NEWEST,
  };
  const applyCoral = category === PROFILE_LISTINGS_CATEGORY_CORALS || (!category && subcategory);
  if (applyCoral) {
    params.pub_categoryLevel1 = CATEGORY_ID_CORAL;
  } else if (category === PROFILE_LISTINGS_CATEGORY_FISH) {
    params.pub_categoryLevel1 = CATEGORY_ID_FISH;
  } else if (category === PROFILE_LISTINGS_CATEGORY_DRY_GOODS) {
    params.pub_listingType = LISTING_TYPE_DRY_GOODS;
  }
  if (subcategory && applyCoral && PROFILE_LISTINGS_SUBCATEGORY_ID.test(subcategory)) {
    params.pub_categoryLevel2 = subcategory;
  }
  return params;
};

export const profileListingsQueryKey = ({ page, category, subcategory, sort } = {}) =>
  JSON.stringify({ page, category, subcategory, sort });

export const hasActiveProfileListingsQuery = ({ page = 1, category, subcategory, sort } = {}) =>
  page > 1 || !!category || !!subcategory || !!sort;

export const clampProfileListingsPage = (query, pagination) => {
  const totalPages = pagination?.totalPages;
  if (typeof totalPages === 'number' && totalPages >= 1 && query.page > totalPages) {
    return { ...query, page: totalPages };
  }
  return query;
};

export const sanitizeProfileListingsSearch = (
  query,
  { typeCounts = {}, childCounts = {}, totalPages } = {}
) => {
  let { page, category, subcategory, sort } = query;
  if (category && !typeCounts[category]) {
    category = null;
    subcategory = null;
  }
  if (subcategory && !childCounts[subcategory]) {
    subcategory = null;
  }
  if (category && category !== PROFILE_LISTINGS_CATEGORY_CORALS) {
    subcategory = null;
  }
  if (typeof totalPages === 'number' && totalPages >= 1 && page > totalPages) {
    page = totalPages;
  }
  return { page, category, subcategory, sort };
};

export { PROFILE_TAB_LISTINGS };
